import { EnumButtonName } from '@calm/evexia-analytics';
import { ChevronRight } from '@mui/icons-material';
import commonMessages from '@rhg/lang/messages/common';
import {
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
} from '@rhg/libs/material';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ContributorAvatar } from '../avatar';
import { Contributor, ContributorType } from '../types';

interface ContributorListButtonProps {
    contributor: Contributor;
    contributorType: ContributorType;
    clickable: true;
    onClick: (contributor: Contributor) => void;
    trackButton: EnumButtonName;
    subtitleOverride?: string;
}
interface ContributorListItemProps {
    contributor: Contributor;
    contributorType: ContributorType;
    subtitleOverride?: string;
    clickable?: false;
    onClick?: undefined;
    trackButton?: undefined;
}
export function ContributorListItem({
    contributor,
    contributorType,
    subtitleOverride,
    clickable,
    onClick,
    trackButton,
}: ContributorListItemProps | ContributorListButtonProps) {
    const { formatMessage } = useIntl();

    const title = useMemo(
        () =>
            contributorType === ContributorType.Author
                ? formatMessage(commonMessages['common.clinical_author'])
                : formatMessage(commonMessages['common.narrator']),
        [contributorType, formatMessage]
    );

    const contents = useMemo(
        () => (
            <>
                <ListItemAvatar>
                    <ContributorAvatar contributor={contributor} size="small" />
                </ListItemAvatar>
                <ListItemText
                    primary={contributor?.name}
                    secondary={subtitleOverride ?? title}
                />
                {clickable && <ChevronRight />}
            </>
        ),
        [clickable, contributor, subtitleOverride, title]
    );

    const handleClick = useCallback(
        () => onClick?.(contributor),
        [contributor, onClick]
    );

    const Container: FC<PropsWithChildren> = useCallback(
        (props) => {
            return clickable ? (
                <ListItemButton
                    onClick={handleClick}
                    trackButton={trackButton}
                    {...props}
                />
            ) : (
                <ListItem {...props} />
            );
        },
        [clickable, handleClick, trackButton]
    );

    return <Container>{contents}</Container>;
}
